<template>
  <FreeBottomSheet
    :name="name"
    persistent
    @onTryClose="onClose"
    @onOpen="syncDraftPassenger"
  >
    <template #header>
      <FreeHeader class="p-4">
        <template #default>
          <h3 class="text-text-neutral-primary text-base font-medium">
            {{ $t('passengersData.multiPassengers.title') }}
          </h3>
        </template>
        <template #rightAction>
          <FreeCloseButton @click="onClose" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="space-y-6 p-4">
        <PassengerFormContainer
          :is-domestic="effectiveIsDomestic"
          :passenger="draftPassenger"
          :passenger-key="passengerKey"
          :validations="validations"
          :provider-class="providerClass"
          @onPassengerUpdate="onPassengerUpdate"
        />
        <FreeButton
          data-qa-id="passengersData.passengerForm.save"
          type="button"
          class="green-main-button w-full"
          @click="onSubmit"
        >
          {{ $t('general.save') }}
        </FreeButton>
      </div>
      <ConfirmationModal
        :modal-name="unsavedModalName"
        class="top-auto h-screen"
        @onAgree="onBottomSheetCloseAgree"
      />
      <FreeAlert
        ref="duplicatePaxAlertRef"
        class="fixed bottom-20 mx-4 flex grow"
        :duration="3000"
      >
        <template #icon>
          <img
            src="@/assets/images/red-warning.svg"
            width="24"
            height="24"
            alt=""
            loading="lazy"
            decoding="async"
          />
        </template>
        <template #default>
          <p class="text-text-neutral-primary">
            {{ $t('passengersData.duplicatePaxError') }}
          </p>
        </template>
      </FreeAlert>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useVuelidate } from '@vuelidate/core';
  import {
    FreeHeader,
    FreeButton,
    FreeBottomSheet,
    FreeCloseButton,
    ConfirmationModal,
    FreeAlert,
  } from 'freedom-shared';
  import { v4 as uuid } from 'uuid';
  import { clone, isEqual } from 'lodash-es';
  import {
    passengerValidation,
    controllerModel,
    citizenshipEntity,
  } from 'shared';
  import type { Airflow } from '@aviata/platform-types';
  import type { PassengerKey, Passenger } from '@aviata/booking-data';

  import { bookingStateModel } from '@/features/booking-state';

  import {
    formPassengerConfig,
    formPassengerModel,
  } from '@/entities/form-passenger';

  import { useUserStore } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';

  import {
    convertPassengerToPayload,
    isSamePersistedPassenger,
    localPassengersStorage,
  } from '../lib';
  import PassengerFormContainer from './PassengerFormContainer.vue';

  const props = defineProps<{
    name: string;
    savedPassengersBottomSheetName: string;
    allPassengers: Passenger[];
    passengerOnCard: Passenger | null;
    passengerKey: PassengerKey;
    passengerIdx: number | null;
    isDomestic: boolean;
    schema: Airflow.Offer.Schema.PassengerEntity;
    providerClass?: string;
    isNewPassenger?: boolean;
    onEditCallback?: (payload: {
      success: boolean;
      passenger?: Passenger;
    }) => void;
  }>();

  const emit = defineEmits<{
    onSubmit: [];
    onPassengerEdit: [updatedPassenger: Passenger, idx: number];
    onPassengerCreate: [passenger: Passenger];
  }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
  const modal = controllerModel.useModal();
  const unsavedModalName = `unsaved-data-modal-${uuid()}`;

  const bookingStore = bookingStateModel.useBookingStore();

  const selectedPassenger = computed<Passenger | null>(() =>
    props.passengerIdx !== null
      ? props.allPassengers[props.passengerIdx]
      : (props.passengerOnCard ?? formPassengerConfig.emptyPassenger),
  );
  const draftPassenger = ref(props.passengerOnCard);

  // Effective domestic status accounts for the live draft citizenship: a domestic
  // flight only allows ID-card documents for KZ citizens.
  const effectiveIsDomestic = computed(
    () =>
      props.isDomestic &&
      draftPassenger.value?.citizenship ===
        citizenshipEntity.config.KAZAKHSTAN_CODE,
  );

  // Храним исходное значение пассажира при открытии формы для корректной проверки изменений
  const initialPassengerOnCard = ref<Passenger | null>(
    props.passengerOnCard ? clone(props.passengerOnCard) : null,
  );

  const duplicatePaxAlertRef = ref();
  const handleDuplicatePaxError = () => {
    duplicatePaxAlertRef.value?.show();
  };

  const validationRules = passengerValidation.lib.initValidation({
    schema: props.schema,
    passengerKey: props.passengerKey,
    validationMessagePath: 'passengersData.validations',
    citizenship: computed(() => draftPassenger.value?.citizenship),
    departureDate: computed(() => bookingStore.lastFlightDepartureDate),
    providerClass: props.providerClass,
  });

  const validations = useVuelidate(
    validationRules,
    computed(() => draftPassenger.value),
  );

  const onPassengerUpdate = (updatedPassenger: Passenger) =>
    (draftPassenger.value = updatedPassenger);

  const syncDraftPassenger = () => {
    draftPassenger.value = clone(selectedPassenger.value);
    // For new passengers on international flights, prefill "N" — almost every
    // KZ international passport number starts with it. The user can still
    // clear or overwrite the input.
    if (
      props.isNewPassenger &&
      !props.isDomestic &&
      draftPassenger.value &&
      !draftPassenger.value.document.number
    ) {
      draftPassenger.value = {
        ...draftPassenger.value,
        document: {
          ...draftPassenger.value.document,
          number: 'N',
        },
      };
    }
    // Сохраняем исходное значение при синхронизации
    initialPassengerOnCard.value = props.passengerOnCard
      ? clone(props.passengerOnCard)
      : null;

    if (props.isNewPassenger) {
      validations.value.$reset();
    }
  };
  const onClose = () => {
    const hasUnsavedData = !isEqual(
      selectedPassenger.value,
      draftPassenger.value,
    );
    if (hasUnsavedData) {
      modal.show(unsavedModalName, {
        title: t('passengersData.multiPassengers.unsavedData.title'),
        message: t('passengersData.multiPassengers.unsavedData.description'),
      });
    } else {
      draftPassenger.value = props.passengerOnCard;
      bottomSheet.hide(props.name);
    }
  };

  watch(
    () => props.passengerOnCard,
    newVal => {
      // Обновляем draftPassenger только если форма не открыта,
      // чтобы не перезаписывать изменения пользователя во время редактирования
      // initialPassengerOnCard обновляется только при открытии формы через syncDraftPassenger
      if (!bottomSheet.isVisible(props.name)) {
        draftPassenger.value = newVal;
      }
    },
    { deep: true },
  );

  const onBottomSheetCloseAgree = () => {
    draftPassenger.value = props.passengerOnCard;
    bottomSheet.hide(props.name);
  };

  const createPassengerMutation = formPassengerModel.useCreatePassenger();
  const editPassengerMutation = formPassengerModel.useEditPassenger();

  const userStore = useUserStore();

  const isNewPaxUnique = (newPax: Passenger) => {
    // Empty document number means the user hasn't entered one yet — don't
    // treat "no doc" rows as colliding with each other.
    if (!newPax.document.number) {
      return true;
    }
    // Match against any other passenger in the list (including the same
    // saved-passenger source when its position differs) so editing an
    // existing passenger to a colliding document number is caught too.
    return !props.allPassengers.some((otherPax, idx) => {
      if (idx === props.passengerIdx) {
        return false;
      }
      return isSamePersistedPassenger(otherPax, newPax);
    });
  };

  const onSubmit = async () => {
    validations.value.$touch();

    let hasError = validations.value.$error;

    if (!hasError && draftPassenger.value) {
      if (isNewPaxUnique(draftPassenger.value)) {
        const userId = await userStore.getUserId();
        const mappedPassenger = convertPassengerToPayload({
          passenger: draftPassenger.value,
        });

        if (props.isNewPassenger) {
          const draftPassengerWithLocalId = {
            ...draftPassenger.value,
            id: uuid(),
          };
          localPassengersStorage.upsert(draftPassengerWithLocalId);
          emit('onPassengerCreate', draftPassengerWithLocalId);
          if (userId) {
            createPassengerMutation
              .createPassengerWithCallback(
                { userId, passenger: mappedPassenger },
                createdPassenger => {
                  const passengerWithBackendId = {
                    ...draftPassengerWithLocalId,
                    id: createdPassenger.id,
                  };
                  localPassengersStorage.upsert(passengerWithBackendId);
                  emit('onPassengerCreate', passengerWithBackendId);
                },
              )
              .catch(() => {
                // Ошибка уже обработана в мутации / API
              });
          }
        } else {
          emit('onPassengerEdit', draftPassenger.value, props.passengerIdx);
          // Сравниваем с исходным значением, а не с текущим passengerOnCard,
          // так как passengerOnCard может обновляться через watch
          const editedPassenger = draftPassenger.value;
          const hasChanges =
            initialPassengerOnCard.value &&
            !isEqual(editedPassenger, initialPassengerOnCard.value);
          if (hasChanges) {
            // Capture the parent handler before bottomSheet.hide() — by the
            // time the API responds this component may be unmounted (v-if),
            // making emit() a no-op. A plain function ref from the parent scope
            // survives component unmount.
            const editCallback = props.onEditCallback;
            if (userId) {
              editPassengerMutation.editPassengerWithCallbacks(
                { userId, passenger: mappedPassenger },
                {
                  onSuccess: () =>
                    editCallback?.({
                      success: true,
                      passenger: editedPassenger,
                    }),
                  onError: () => editCallback?.({ success: false }),
                },
              );
            } else {
              editCallback?.({ success: true, passenger: editedPassenger });
            }
          }
        }
        bottomSheet.hide(props.name);
      } else {
        hasError = true;
        handleDuplicatePaxError();
      }
    }
    analyticsEventsLib.savedPaxSaveClick({
      has_error: hasError,
      firstName: draftPassenger.value?.firstName,
      lastName: draftPassenger.value?.lastName,
      providerClass: props.providerClass,
    });
  };
</script>
